import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';
import { buildHeightField, heightFieldToNormalPixels } from '../utils/waterNormals';

/**
 * Deterministic procedural texture builders for the ship materials.
 *
 * Every texture is generated on a 2D canvas from a seeded RNG (never
 * `Math.random`), so two builds with the same seed produce identical pixels and
 * the Playwright visual baselines stay stable. The colour maps are intentionally
 * greyscale-ish "detail" maps: the material multiplies them by a per-ship
 * palette colour so one shared grain/weave map can be reused by every vessel.
 */

function requireContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create a 2D canvas context.');
  return context;
}

function makeCanvas(size: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  return canvas;
}

/** Wrap + mark a canvas texture as a colour map (sRGB encoded). */
function finishColorTexture(canvas: HTMLCanvasElement, repeat = 1): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(repeat, repeat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** Wrap + mark a canvas texture as linear data (roughness / normals). */
function finishDataTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** One horizontal plank band in the shared wood layout (albedo + AO). */
interface PlankBand {
  y0: number;
  y1: number;
  shade: number;
}

/**
 * Deterministic horizontal plank layout shared by the wood albedo and the
 * ambient-occlusion map so their seams stay pixel-aligned. Same seed, same
 * band sequence.
 */
function plankBands(size: number, seed: number): PlankBand[] {
  const random = createSeededRandom(seed);
  const planks: PlankBand[] = [];
  let y = 0;
  while (y < size) {
    const height = 6 + Math.floor(random() * 10);
    planks.push({ y0: y, y1: Math.min(size, y + height), shade: 0.78 + random() * 0.2 });
    y += height;
  }
  return planks;
}

/** Read the red channel of a procedural canvas texture as 8-bit samples. */
function readRedChannel(texture: THREE.Texture, size: number): Uint8ClampedArray {
  const canvas = texture.image as HTMLCanvasElement;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Could not read a 2D canvas context from the texture.');
  const { data } = ctx.getImageData(0, 0, size, size);
  const red = new Uint8ClampedArray(size * size);
  for (let i = 0; i < red.length; i += 1) red[i] = data[i * 4];
  return red;
}

/**
 * A high-contrast wood grain detail map. Horizontal planks with wavy grain
 * lines, seams and a few knots. Authored in greyscale so `material.color`
 * provides the wood tone.
 */
export function createWoodGrainTexture(size = 256, seed = 1337): THREE.CanvasTexture {
  const random = createSeededRandom(seed);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);

  // Plank bands: each has an independent base luminance and height.
  const planks = plankBands(size, seed);

  for (const plank of planks) {
    const lum = Math.round(plank.shade * 255);
    ctx.fillStyle = `rgb(${lum},${lum},${lum})`;
    ctx.fillRect(0, plank.y0, size, plank.y1 - plank.y0);

    // Wavy grain lines following the plank.
    const lineCount = 3 + Math.floor(random() * 3);
    for (let i = 0; i < lineCount; i += 1) {
      const gy = plank.y0 + 1 + random() * (plank.y1 - plank.y0 - 2);
      const gl = Math.max(0, Math.round((plank.shade - 0.2 - random() * 0.12) * 255));
      ctx.strokeStyle = `rgba(${gl},${gl},${gl},0.6)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      const phase = random() * Math.PI * 2;
      const freq = 0.02 + random() * 0.035;
      const amp = 0.5 + random() * 1.1;
      for (let x = 0; x <= size; x += 8) {
        const yy = gy + Math.sin(x * freq + phase) * amp;
        if (x === 0) ctx.moveTo(x, yy);
        else ctx.lineTo(x, yy);
      }
      ctx.stroke();
    }

    // Plank seam.
    const seam = Math.max(0, Math.round((plank.shade - 0.35) * 255));
    ctx.strokeStyle = `rgba(${seam},${seam},${seam},0.9)`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, plank.y0 + 0.5);
    ctx.lineTo(size, plank.y0 + 0.5);
    ctx.stroke();
  }

  // A few knots for character.
  const knotCount = 3 + Math.floor(random() * 2);
  for (let i = 0; i < knotCount; i += 1) {
    const kx = random() * size;
    const ky = random() * size;
    const kr = 2 + random() * 3;
    ctx.fillStyle = 'rgba(70,52,32,0.55)';
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr, kr * 0.72, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(46,34,20,0.55)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr * 0.5, kr * 0.36, 0, 0, Math.PI * 2);
    ctx.stroke();
  }

  return finishColorTexture(canvas);
}

/**
 * A woven-canvas detail map: near-white cloth with a subtle grid and soft
 * grime blotches. Tinted by the sail palette colour at draw time.
 */
export function createCanvasWeaveTexture(size = 128, seed = 1337): THREE.CanvasTexture {
  const random = createSeededRandom(seed);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);

  ctx.fillStyle = 'rgb(246,245,240)';
  ctx.fillRect(0, 0, size, size);

  // Weave threads.
  const thread = 4;
  for (let i = 0; i < size; i += thread) {
    ctx.fillStyle = 'rgba(214,212,204,0.4)';
    ctx.fillRect(i, 0, 1, size);
    ctx.fillRect(0, i, size, 1);
  }

  // Soft grime.
  const blotchCount = 5 + Math.floor(random() * 4);
  for (let i = 0; i < blotchCount; i += 1) {
    const x = random() * size;
    const by = random() * size;
    const r = 4 + random() * 10;
    ctx.fillStyle = `rgba(170,166,156,${0.04 + random() * 0.05})`;
    ctx.beginPath();
    ctx.ellipse(x, by, r, r * 0.8, random() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }

  return finishColorTexture(canvas);
}

/**
 * A smooth tileable roughness map (fBm value noise), greyscale in every
 * channel so it also reads correctly as a metalness map if reused.
 */
export function createRoughnessTexture(
  size = 128,
  seed = 1337,
  min = 0.4,
  max = 0.95,
): THREE.CanvasTexture {
  const field = buildHeightField(size, seed);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);
  const image = ctx.createImageData(size, size);
  for (let i = 0; i < field.length; i += 1) {
    const value = Math.min(1, Math.max(0, min + (max - min) * field[i]));
    const channel = Math.round(value * 255);
    const offset = i * 4;
    image.data[offset] = channel;
    image.data[offset + 1] = channel;
    image.data[offset + 2] = channel;
    image.data[offset + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return finishDataTexture(canvas);
}

/**
 * A tileable tangent-space normal map derived from the same seeded fBm field
 * family as the roughness map, keeping the two visually coherent.
 */
export function createNormalTexture(size = 128, seed = 1337, strength = 1.6): THREE.CanvasTexture {
  const pixels = heightFieldToNormalPixels(buildHeightField(size, seed), size, strength);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);
  const image = ctx.createImageData(size, size);
  image.data.set(pixels);
  ctx.putImageData(image, 0, 0);
  return finishDataTexture(canvas);
}

/**
 * Tileable ambient-occlusion detail map for the wooden surfaces.
 *
 * Authored for the red channel of the packed ORM texture: 255 = fully open,
 * lower = more occluded. The recessed plank seams cast a soft vertical shadow
 * into each plank, aligned with the wood albedo's plank layout, plus a gentle
 * fBm mottling so the gaps between seams still read as hand-built timber.
 */
export function createAmbientOcclusionTexture(size = 128, seed = 1337): THREE.CanvasTexture {
  const planks = plankBands(size, seed);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);

  // Base near-white: fully open geometry.
  ctx.fillStyle = 'rgb(244,244,244)';
  ctx.fillRect(0, 0, size, size);

  for (const plank of planks) {
    const height = plank.y1 - plank.y0;
    const falloff = Math.max(1, Math.min(6, height));
    const gradient = ctx.createLinearGradient(0, plank.y0, 0, plank.y0 + falloff);
    gradient.addColorStop(0, 'rgba(102,102,102,1)');
    gradient.addColorStop(1, 'rgba(244,244,244,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, plank.y0, size, falloff);
    // Sharp contact lip where the plank above meets the next one.
    ctx.fillStyle = 'rgba(128,128,128,1)';
    ctx.fillRect(0, plank.y0, size, 1);
  }

  // Subtle grain-level occlusion so the surface is not flat between seams.
  const field = buildHeightField(size, seed ^ 0x51a7c);
  const image = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < field.length; i += 1) {
    const scale = 1 - 0.14 * field[i];
    const offset = i * 4;
    image.data[offset] = Math.min(255, Math.round(image.data[offset] * scale));
    image.data[offset + 1] = Math.min(255, Math.round(image.data[offset + 1] * scale));
    image.data[offset + 2] = Math.min(255, Math.round(image.data[offset + 2] * scale));
  }
  ctx.putImageData(image, 0, 0);
  return finishDataTexture(canvas);
}

/**
 * Pack AO (R) + roughness (G) + metalness (B) into one RGB texture — the ORM
 * convention the material rules rely on. three r184 samples `.r` for the AO
 * map, `.g` for the roughness map and `.b` for the metalness map, so a single
 * shared texture can drive all three and save two texture slots per material.
 */
export function packOrmTexture(
  ao: THREE.Texture,
  roughness: THREE.Texture,
  metalness: number | THREE.Texture = 0,
  size = 128,
): THREE.CanvasTexture {
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);
  const aoRed = readRedChannel(ao, size);
  const roughnessRed = readRedChannel(roughness, size);
  const metalnessRed = typeof metalness === 'number' ? null : readRedChannel(metalness, size);
  const metalnessByte =
    typeof metalness === 'number' ? Math.round(Math.min(1, Math.max(0, metalness)) * 255) : 0;

  const image = ctx.createImageData(size, size);
  for (let i = 0; i < size * size; i += 1) {
    const offset = i * 4;
    image.data[offset] = aoRed[i];
    image.data[offset + 1] = roughnessRed[i];
    image.data[offset + 2] = metalnessRed ? metalnessRed[i] : metalnessByte;
    image.data[offset + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return finishDataTexture(canvas);
}

/**
 * Deterministic hull-side detail map: planking strakes, pronounced seams and
 * weather stains. Authored in light greys so the per-vessel palette still tints
 * through, while the darker seams and streaks survive the multiplication and
 * read as a weathered, built hull rather than a flat slab. Shared by every
 * ship so the two vessels never duplicate this 256×256 canvas texture.
 */
export function createHullSideTexture(seed = 1337, size = 256): THREE.CanvasTexture {
  const random = createSeededRandom(seed);
  const canvas = makeCanvas(size);
  const ctx = requireContext(canvas);

  ctx.fillStyle = 'rgb(191,180,158)';
  ctx.fillRect(0, 0, size, size);

  // Planking strakes: horizontal bands map to fore-aft planks on the hull.
  let y = 0;
  while (y < size) {
    const height = 6 + Math.floor(random() * 9);
    const shade = 0.8 + random() * 0.18;
    const lum = Math.round(shade * 255);
    ctx.fillStyle = `rgb(${lum},${lum},${lum})`;
    ctx.fillRect(0, y, size, height);

    // Wavy grain inside each plank.
    const lineCount = 2 + Math.floor(random() * 3);
    for (let i = 0; i < lineCount; i += 1) {
      const gy = y + 1 + random() * (height - 2);
      const gl = Math.max(0, Math.round((shade - 0.22 - random() * 0.12) * 255));
      ctx.strokeStyle = `rgba(${gl},${gl},${gl},0.55)`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      const phase = random() * Math.PI * 2;
      const freq = 0.02 + random() * 0.03;
      const amp = 0.5 + random() * 1.0;
      for (let x = 0; x <= size; x += 8) {
        const yy = gy + Math.sin(x * freq + phase) * amp;
        if (x === 0) ctx.moveTo(x, yy);
        else ctx.lineTo(x, yy);
      }
      ctx.stroke();
    }

    // Pronounced plank seam.
    ctx.fillStyle = 'rgba(34,27,18,0.9)';
    ctx.fillRect(0, y, size, 1.6);
    y += height;
  }

  // Knots.
  const knotCount = 3 + Math.floor(random() * 3);
  for (let i = 0; i < knotCount; i += 1) {
    const kx = random() * size;
    const ky = random() * size;
    const kr = 2 + random() * 3;
    ctx.fillStyle = 'rgba(66,52,34,0.5)';
    ctx.beginPath();
    ctx.ellipse(kx, ky, kr, kr * 0.72, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Weathering: water stains streaking down from each gunwale toward the keel.
  const streakCount = 12 + Math.floor(random() * 8);
  for (let i = 0; i < streakCount; i += 1) {
    const x = random() * size;
    const width = 1 + random() * 3;
    const length = size * (0.2 + random() * 0.5);
    const fromTop = i % 2 === 0;
    const gradient = ctx.createLinearGradient(0, fromTop ? 0 : size, 0, fromTop ? length : size - length);
    gradient.addColorStop(0, 'rgba(40,34,26,0.55)');
    gradient.addColorStop(1, 'rgba(40,34,26,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(x, fromTop ? 0 : size - length, width, length);
  }

  // Salt-bleach blotches near the waterline.
  const blotchCount = 10 + Math.floor(random() * 6);
  for (let i = 0; i < blotchCount; i += 1) {
    const x = random() * size;
    const by = size * (0.4 + random() * 0.2);
    const r = 3 + random() * 8;
    ctx.fillStyle = `rgba(224,220,206,${0.05 + random() * 0.06})`;
    ctx.beginPath();
    ctx.ellipse(x, by, r, r * 0.6, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  // Fewer U repeats keep the weathering streaks from reading as a regular grid.
  texture.repeat.set(0.5, 1);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}
